<script>
  import { Html, LayerCake, Svg } from "layercake";
  import { AxisX, Tooltip } from "@urbaninstitute/dataviz-components";
  import Beeswarm from "./Beeswarm.svelte";
  import { useData } from "$utils/data.svelte.js";
  import { disasterMetadata, no_disasters_text } from "$data/consts.js";
  import { scaleOrdinal } from "d3";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";

  let toolData = useData();

  let data = $derived(toolData.isReady() ? toolData.myDisasterData : []);
  let legendData = $derived(
    disasterMetadata.filter((d) => data.some((item) => item.type === d.type))
  );

  const xKey = "year_declared";
  const zKey = "type";

  const r = 10;

  const seriesColors = $derived(legendData.map((d) => d.color));
  const seriesNames = $derived(legendData.map((d) => d.label));

  let tooltip = $state(null);

  // Dismiss the tooltip on scroll instead of letting it drift, since its position
  // is only computed once (from the triggering event) and never re-synced.
  $effect(() => {
    if (typeof window === "undefined") return;
    const clearTooltip = () => (tooltip = null);
    window.addEventListener("scroll", clearTooltip, { passive: true });
    return () => window.removeEventListener("scroll", clearTooltip);
  });

  function formatAreas(areaString) {
    const areas = areaString.split("; ");
    if (areas.length === 1) return areas[0];
    if (areas.length === 2) return `${areas[0]} and ${areas[1]}`;
    return areas.slice(0, -1).join("; ") + "; and " + areas[areas.length - 1];
  }
</script>

{#if data.length > 0}
  <div class="chart-legend-container">
    <p class="chart-title">
      Presidential disaster declarations that covered {toolData.activeCommunityNameShort}, 2014–26
    </p>
    <div class="legend-container">
      <ColorLegend
        scale={scaleOrdinal().domain(seriesNames).range(seriesColors)}
        swatch
        swatchCircle
        swatchSize={16}
      />
    </div>
    <div class="chart-container">
      <LayerCake
        padding={{ bottom: 15, top: 25 }}
        x={xKey}
        xDomain={[2014, 2026]}
        z={zKey}
        zScale={scaleOrdinal()}
        zRange={seriesColors}
        zDomain={seriesNames}
        custom={{ getColor: (type) => toolData.getColorFromType(type) }}
        // zDomainSort={true}
        {data}
      >
        <Html>
          {#if tooltip}
            <Tooltip x={tooltip.x} y={tooltip.y} size="large">
              <div class="tooltip-content">
                <p class="tooltip-heading">{tooltip.year}</p>
                <p class="tooltip-text">
                  {#if tooltip.applicantIsStateOrTerritory}
                    {tooltip.applicantTakesArticle ? "The " : ""}{tooltip.applicant} received a presidential declaration for
                    {tooltip.type == "earthquake" ? "an" : "a"}
                    <span class="underlined" style:border-bottom="3px solid {tooltip.color}"
                      ><b>{tooltip.type}</b></span
                    > event, which covered {formatAreas(tooltip.area)}.
                  {:else}
                    {tooltip.applicantTakesArticle ? "The " : ""}{tooltip.applicant} received a presidential declaration for
                    {tooltip.type == "earthquake" ? "an" : "a"}
                    <span class="underlined" style:border-bottom="3px solid {tooltip.color}"
                      ><b>{tooltip.type}</b></span
                    > event.
                  {/if}
                </p>
              </div>
            </Tooltip>
          {/if}
        </Html>
        <Svg>
          <AxisX ticks={[2014, 2016, 2018, 2020, 2022, 2024, 2026]} />
          <Beeswarm {r} strokeWidth={1} xStrength={0.95} yStrength={0.075} bind:tooltip />
        </Svg>
      </LayerCake>
    </div>
  </div>
{:else}
  <p>{no_disasters_text}</p>
{/if}

<style>
  .chart-legend-container {
    background-color: white;
    padding: 24px;
    p.chart-title {
      font-weight: var(--font-weight-bold);
      font-size: 16px;
      line-height: 150%;
      margin-bottom: 32px;
      margin-top: 0;
    }
  }
  .chart-container {
    margin-top: 30px;
    width: 100%;
    height: 200px;
  }
  .tooltip-content {
    margin: 0 12px 12px 12px;
    p.tooltip-heading {
      font-weight: var(--font-weight-bold);
      margin-bottom: 0px;
    }
    p.tooltip-text {
      line-height: 150%;
    }
  }
</style>
